import React, { useRef, useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { caseStudies as caseStudiesSource } from "../../data/caseStudies";

// Home carousel uses the shared case-studies data source so slugs always stay in sync with /work/:id.
const caseStudies = caseStudiesSource.map((c) => ({
  id: c.id,
  tag: c.tag,
  title: c.title,
  desc: c.subtitle,
  stat: c.stat,
  statLabel: c.statLabel,
  accent: c.accent,
  img: c.img,
}));

export default function CaseStudies() {
  const scrollerRef = useRef(null);
  const [active, setActive] = useState(0);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);

  const updateControls = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
    const cardW = el.firstChild?.clientWidth || 1;
    const gap = 20;
    const idx = Math.round(el.scrollLeft / (cardW + gap));
    setActive(Math.min(idx, caseStudies.length - 1));
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateControls, { passive: true });
    updateControls();
    return () => el.removeEventListener("scroll", updateControls);
  }, [updateControls]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cardW = el.firstChild?.clientWidth || el.clientWidth * 0.7;
    el.scrollBy({ left: dir * (cardW + 20), behavior: "smooth" });
  };

  const scrollToIdx = (i) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cardW = el.firstChild?.clientWidth || el.clientWidth * 0.7;
    el.scrollTo({ left: i * (cardW + 20), behavior: "smooth" });
  };

  return (
    <section id="work" data-testid="case-studies" className="section-light py-section relative overflow-hidden">
      <div className="bg-orbs bg-orbs-alt" />
      <div className="container-xl mb-10 md:mb-14 relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#86868B] mb-4">
              Featured case studies
            </div>
            <h2 className="font-display text-[#1D1D1F] text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] font-semibold max-w-3xl">
              Real growth.{" "}
              <span className="font-serif italic font-light text-[#86868B]">
                Measured and repeatable.
              </span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/work"
              data-testid="case-studies-explore"
              className="btn-pill btn-ghost-light text-sm"
            >
              Explore all work
              <ArrowUpRight size={14} strokeWidth={2.2} />
            </a>
          </div>
        </div>
      </div>

      <div
        ref={scrollerRef}
        data-testid="case-studies-scroll"
        className="scroll-snap-x flex gap-5 overflow-x-auto px-6 lg:px-12 pb-6"
      >
        {caseStudies.map((cs, i) => (
          <motion.a
            key={cs.id}
            href={`/work/${cs.id}`}
            data-testid={`case-study-${cs.id}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
            className="group relative shrink-0 w-[86vw] md:w-[560px] lg:w-[680px] aspect-[4/5] rounded-[32px] overflow-hidden bg-black"
          >
            <img
              src={cs.img}
              alt={cs.title}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-[1200ms]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-transparent" />
            <div
              className="absolute inset-x-0 top-0 h-px opacity-80"
              style={{
                background: `linear-gradient(90deg, transparent, ${cs.accent}, transparent)`,
              }}
            />
            <div className="absolute inset-0 p-8 md:p-12 flex flex-col justify-between text-white">
              <div className="flex items-start justify-between">
                <span
                  className="text-[10px] uppercase tracking-[0.24em] backdrop-blur-md border rounded-full px-3 py-1.5"
                  style={{
                    background: `${cs.accent}18`,
                    borderColor: `${cs.accent}55`,
                    color: cs.accent,
                  }}
                >
                  <span
                    className="chip-dot"
                    style={{ background: cs.accent }}
                  />
                  {cs.tag}
                </span>
                <div className="text-right">
                  <div
                    className="font-display text-4xl md:text-5xl tracking-tighter font-semibold"
                    style={{ color: cs.accent }}
                  >
                    {cs.stat}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mt-1">
                    {cs.statLabel}
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[1.05] font-medium max-w-md">
                  {cs.title}
                </h3>
                <p className="mt-4 text-white/75 text-sm md:text-base max-w-md leading-relaxed">
                  {cs.desc}
                </p>
                <div
                  className="mt-6 inline-flex items-center gap-2 text-sm font-medium transition-transform group-hover:translate-x-1"
                  style={{ color: cs.accent }}
                >
                  Read case study
                  <ArrowUpRight size={14} strokeWidth={2.2} />
                </div>
              </div>
            </div>
          </motion.a>
        ))}
      </div>

      {/* Controls */}
      <div className="container-xl mt-8 md:mt-10 flex flex-col md:flex-row items-center md:justify-between gap-6">
        <div className="flex items-center gap-2">
          {caseStudies.map((cs, i) => (
            <button
              key={cs.id}
              data-testid={`case-dot-${i}`}
              onClick={() => scrollToIdx(i)}
              aria-label={`Go to ${cs.title}`}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: active === i ? 40 : 14,
                background: active === i ? cs.accent : "rgba(0,0,0,0.18)",
              }}
            />
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => scrollBy(-1)}
            disabled={!canLeft}
            data-testid="case-prev"
            className="h-12 w-12 rounded-full border border-black/15 flex items-center justify-center hover:bg-[#1D1D1F] hover:text-white hover:border-[#1D1D1F] transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#1D1D1F]"
            aria-label="Previous case study"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="text-sm text-[#86868B] font-mono tabular-nums px-2">
            {String(active + 1).padStart(2, "0")} /{" "}
            {String(caseStudies.length).padStart(2, "0")}
          </div>
          <button
            onClick={() => scrollBy(1)}
            disabled={!canRight}
            data-testid="case-next"
            className="h-12 w-12 rounded-full border border-black/15 flex items-center justify-center hover:bg-[#1D1D1F] hover:text-white hover:border-[#1D1D1F] transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#1D1D1F]"
            aria-label="Next case study"
          >
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}
